import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NaturalMortalitySlider } from '../common/SliderControls/SliderControls';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import ParameterTracking from '../ParameterTracking/ParameterTracking';

// ParameterTracking is .jsx; optional props are inferred as null-only from defaults
const ParameterTrackingAny = ParameterTracking as React.ComponentType<{
  parameterName: string;
  stockName?: string;
  previewMode?: boolean;
  onPointClick?: (mValue: number) => void;
  currentDbValue?: number;
}>;

const SLUG = 'groundfish-natural-mortality';
const PARAMETER_NAME = 'Natural Mortality Rate';

interface GroundfishNaturalMortalityData {
  naturalMortalityValue?: number;
  dbNaturalMortalityValue?: number;
  updatingPStar?: boolean;
  selectedStockId?: number | string | null;
}

export default function GroundfishNaturalMortality(props: RegistryComponentProps): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<GroundfishNaturalMortalityData>(props);
  const pageUpdatingPStar = pageData?.data?.updatingPStar as boolean | undefined;
  const pageSelectedStockId = pageData?.data?.selectedStockId as number | string | null | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;

  const [showModal, setShowModal] = useState(false);
  const sliderContainerRef = useRef<HTMLDivElement>(null);

  const dbNaturalMortalityValue = data.dbNaturalMortalityValue ?? 0.2;
  const formValue = formContext?.values?.[SLUG] as number | undefined;
  const naturalMortalityValue =
    formValue ?? data.naturalMortalityValue ?? dbNaturalMortalityValue ?? 0.2;

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value);
      formContext?.setValue(SLUG, newValue);
    },
    [formContext]
  );

  const handleChartPointClick = React.useCallback(
    (mValue: number) => {
      formContext?.setValue(SLUG, mValue);
    },
    [formContext]
  );

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  const updatingPStar = data.updatingPStar ?? pageUpdatingPStar ?? false;
  const selectedStockId = data.selectedStockId ?? pageSelectedStockId ?? null;
  const stockName = pageStockName ?? 'All Stocks';

  return (
    <div ref={sliderContainerRef} className="noscroll-natural-mortality-slider-container">
      <NaturalMortalitySlider
        naturalMortalityValue={naturalMortalityValue}
        dbNaturalMortalityValue={dbNaturalMortalityValue}
        handleNaturalMortalityChange={handleChange}
        updatingPStar={updatingPStar}
        selectedStockId={selectedStockId}
        onInsightsClick={(() => setShowModal(true)) as never}
      />

      {showModal &&
        createPortal(
          <Modal
            title={`Research Data for ${PARAMETER_NAME}`}
            onClose={handleCloseModal}
            size="large"
            transparentBackdrop={true}
            {...({ positionRef: sliderContainerRef, highlightElement: sliderContainerRef } as Record<string, unknown>)}
            parameterStyle={true}
            headerStyle={{
              marginBottom: '20px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                marginBottom: '15px',
                padding: '10px',
                background: '#f0f9ff',
                borderRadius: '6px',
                border: '1px solid #bae6fd',
              }}
            >
              <p style={{ margin: 0, fontSize: '14px', color: '#0369a1' }}>
                <strong>💡 Tip:</strong> Click on any data point in the chart to set the Natural Mortality slider to that value.
              </p>
            </div>
            <ParameterTrackingAny
              parameterName={PARAMETER_NAME}
              stockName={stockName}
              previewMode={true}
              onPointClick={handleChartPointClick}
              currentDbValue={dbNaturalMortalityValue}
            />
          </Modal>,
          document.body
        )}
    </div>
  );
}
